import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { listMySubmissions } from '../gallery/galleryApi';
import { useCurrentUser } from '../account/useAuth';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { getSubmissionFeedback, submissionBlobUrl } from '../teacher/teacherApi';
import type { FeedbackRead } from '../teacher/teacherApi';
import './MyFeedbackPage.css';

/** Page shell so every render path of MyFeedback (loading, signed-out,
 *  list) shows the BytaApps brand top + footer. */
function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <div className="bl-page">
      <AppHeader subBrand="Feedback" privacy="on-device" />
      {children}
      <AppFooter />
    </div>
  );
}

/**
 * Kid-side feedback dashboard — `/me/feedback`.
 *
 * Lists the kid's submissions with the teacher's rubric marks +
 * overall comment surfaced inline. The kid sees the same level
 * descriptions the teacher saw at grading time (server-side
 * snapshot, see `submission_rubric_marks.criterion_description`).
 *
 * Status badge framing is kid-friendly:
 *   * "waiting on your teacher" — awaiting_teacher_review
 *   * "checking…"               — processing
 *   * "approved ✓"              — approved
 *   * "returned"                — rejected (with the teacher's note)
 *
 * Visible to any signed-in kid. Non-classroom accounts (parent-linked
 * children, free-play kids) won't have rubric marks attached, so the
 * page falls back to "comment + status" display gracefully.
 */
export function MyFeedbackPage(): JSX.Element {
  const { data: user, isPending: userPending } = useCurrentUser();
  const subs = useQuery({
    queryKey: ['my-submissions'],
    queryFn: listMySubmissions,
    enabled: Boolean(user),
  });

  if (userPending) {
    return (
      <PageShell>
        <div className="bl-mf" data-testid="mf-loading" />
      </PageShell>
    );
  }
  if (!user) {
    return (
      <PageShell>
        <div className="bl-mf">
          <p className="bl-mf__notice">Sign in to see your feedback.</p>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="bl-mf">
        <header className="bl-mf__header">
          <h1 className="bl-mf__title">Your feedback</h1>
          <p className="bl-mf__subtitle">What your teacher said about your submitted work.</p>
        </header>

        {subs.isPending ? (
          <p className="bl-mf__notice">Loading…</p>
        ) : subs.isError ? (
          <p className="bl-mf__notice">Couldn&apos;t load your submissions.</p>
        ) : !subs.data || subs.data.length === 0 ? (
          <div className="bl-mf__empty">
            <p>You haven&apos;t submitted anything yet.</p>
            <p className="bl-mf__empty-hint">
              From the studio, click &quot;Publish&quot; on a project to send it for review.
            </p>
            <Link to="/studio" className="bl-mf__empty-link">
              Open the studio →
            </Link>
          </div>
        ) : (
          <ul className="bl-mf__list">
            {subs.data.map((s) => (
              <FeedbackRow
                key={s.id}
                submissionId={s.id}
                projectName={s.project_name}
                status={s.status}
                rejectionReason={s.rejection_reason}
                classroomId={user.classroom_id ?? null}
              />
            ))}
          </ul>
        )}
      </div>
    </PageShell>
  );
}

function FeedbackRow(props: {
  submissionId: string;
  projectName: string;
  status: string;
  rejectionReason: string | null;
  classroomId: string | null;
}): JSX.Element {
  // Only fetch rubric feedback for classroom-scoped submissions —
  // the endpoint requires a classroom in the URL. Parent-linked or
  // adult submissions show comment-free status.
  const fb = useQuery({
    queryKey: ['classroom', props.classroomId, 'submission', props.submissionId, 'feedback'],
    queryFn: () => getSubmissionFeedback(props.classroomId as string, props.submissionId),
    enabled: Boolean(props.classroomId),
    // It's fine for this fetch to fail (e.g. rejected submission with
    // no classroom because the kid was moved). Don't retry.
    retry: false,
  });

  const data: FeedbackRead | undefined = fb.data;

  return (
    <li className="bl-mf__row">
      <div className="bl-mf__row-head">
        <h3 className="bl-mf__row-name">{props.projectName}</h3>
        <StatusBadge status={props.status} />
      </div>

      {props.status === 'rejected' && props.rejectionReason ? (
        <p className="bl-mf__rejection">
          <strong>Note from your teacher:</strong> {props.rejectionReason}
        </p>
      ) : null}

      {data && data.project_classes.length > 0 && props.classroomId ? (
        // Mirror of the teacher's "What Avery made" panel, framed
        // for the kid: "What you submitted". Showing this here is
        // load-bearing — the kid's feedback otherwise feels like a
        // verdict pulled from thin air. Seeing the samples next to
        // the rubric levels closes the loop ("oh, that's why I got
        // 'good' on balanced classes — both classes have ~10").
        <div className="bl-mf__samples-block">
          <div className="bl-mf__section-eyebrow">What you submitted</div>
          <ul className="bl-mf__class-list">
            {data.project_classes.map((c) => (
              <li key={c.class_label} className="bl-mf__class-row">
                {c.sample_blob_id && (c.sample_kind === 'image' || c.sample_kind === 'pose') ? (
                  <img
                    className="bl-mf__class-thumb"
                    src={submissionBlobUrl(
                      props.classroomId as string,
                      props.submissionId,
                      c.sample_blob_id,
                    )}
                    alt={`${c.class_label} sample`}
                    loading="lazy"
                  />
                ) : c.sample_blob_id && c.sample_kind === 'audio' ? (
                  // eslint-disable-next-line jsx-a11y/media-has-caption -- raw kid sample, no captions to attach
                  <audio
                    className="bl-mf__class-audio"
                    src={submissionBlobUrl(
                      props.classroomId as string,
                      props.submissionId,
                      c.sample_blob_id,
                    )}
                    controls
                    preload="metadata"
                    aria-label={`Play ${c.class_label} sample`}
                  />
                ) : (
                  <div
                    className="bl-mf__class-thumb bl-mf__class-thumb--missing"
                    aria-hidden="true"
                  />
                )}
                <div className="bl-mf__class-meta">
                  <span className="bl-mf__class-name">{c.class_label}</span>
                  <span className="bl-mf__class-count">
                    {c.sample_count} {c.sample_count === 1 ? 'sample' : 'samples'}
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data && data.comment_md ? (
        <div className="bl-mf__comment-block">
          <div className="bl-mf__section-eyebrow">Comment</div>
          <p className="bl-mf__comment">{data.comment_md}</p>
        </div>
      ) : null}

      {data && data.marks.length > 0 ? (
        <div className="bl-mf__marks-block">
          <div className="bl-mf__section-eyebrow">Rubric</div>
          <ul className="bl-mf__marks">
            {data.marks.map((m) => (
              <li key={m.criterion_key} className="bl-mf__mark">
                <div className="bl-mf__mark-head">
                  <span className="bl-mf__criterion">{m.criterion_label}</span>
                  <span className="bl-mf__level">{m.level_name}</span>
                </div>
                <p className="bl-mf__criterion-desc">{m.criterion_description}</p>
                <p className="bl-mf__level-desc">{m.level_description}</p>
                {m.note_md ? (
                  <p className="bl-mf__note">
                    <strong>Teacher note:</strong> {m.note_md}
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {data &&
      data.marks.length === 0 &&
      !data.comment_md &&
      props.status === 'awaiting_teacher_review' ? (
        <p className="bl-mf__quiet">Your teacher hasn&apos;t graded this yet.</p>
      ) : null}
    </li>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const { label, tone } = humanStatus(status);
  return <span className={`bl-mf__status bl-mf__status--${tone}`}>{label}</span>;
}

function humanStatus(status: string): { label: string; tone: string } {
  switch (status) {
    case 'awaiting_teacher_review':
      return { label: 'waiting on your teacher', tone: 'wait' };
    case 'awaiting_parent_consent':
      return { label: 'waiting on a grown-up', tone: 'wait' };
    case 'processing':
      return { label: 'checking…', tone: 'wait' };
    case 'approved':
      return { label: 'approved ✓', tone: 'good' };
    case 'rejected':
      return { label: 'returned', tone: 'returned' };
    case 'withdrawn':
      return { label: 'pulled', tone: 'wait' };
    default:
      return { label: status, tone: 'wait' };
  }
}
